import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ThemeProvider } from "./context/ThemeContext";
import AdminDashboardPage from "./admin/AdminDashboardPage";
import NexusChatPage from "./admin/NexusChatPage";
import AdminLayout from "./admin/AdminLayout";
import AnalyticsPage from "./admin/AnalyticsPage";
import AssessmentFormPage from "./admin/AssessmentFormPage";
import CompanyProfilePage from "./admin/CompanyProfilePage";
import EngagementWizard from "./admin/EngagementWizard";
import InsightsPage from "./admin/InsightsPage";
import InviteManagementPage from "./admin/InviteManagementPage";
import LoginPage from "./admin/LoginPage";
import OnboardingPage from "./admin/OnboardingPage";
import WorkspaceJoinPage from "./admin/WorkspaceJoinPage";
import AdminOpsPage from "./admin/AdminOpsPage";
import ProtectedRoute from "./admin/ProtectedRoute";
import ReportProfilesPage from "./admin/ReportProfilesPage";
import EmailTemplatesSchedulerPage from "./admin/EmailTemplatesSchedulerPage";
import RoleManagementPage from "./admin/RoleManagementPage";
import UserManagementPage from "./admin/UserManagementPage";
import SurveyTemplatesPage from "./admin/SurveyTemplatesPage";
import { LENS_ROUTES, ROUTES } from "./admin/routes";
import EngagementPage from "./admin/surfaces/EngagementPage";
import PortfolioPage from "./admin/surfaces/PortfolioPage";
import StudioPage from "./admin/surfaces/StudioPage";
import WorkspaceBrandingPage from "./admin/WorkspaceBrandingPage";
import SwitchWorkspacePage from "./admin/SwitchWorkspacePage";
import { isAdminLoggedIn } from "./api/adminClient";
import ParticipantFlow from "./pages/ParticipantFlow";
import SurveyPausePage from "./pages/SurveyPausePage";
import LegacyInviteRedirect from "./components/LegacyInviteRedirect";

function LegacyRedirect({ to }) {
  return <Navigate to={to} replace />;
}

function PublicOnly({ children }) {
  if (isAdminLoggedIn()) {
    return <Navigate to={ROUTES.dashboard} replace />;
  }
  return children;
}

export default function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <Routes>
        <Route path="/a/:token" element={<ParticipantFlow />} />
        <Route
          caseSensitive={false}
          path="/GetStartedPage/:token"
          element={<LegacyInviteRedirect />}
        />
        <Route caseSensitive={false} path="/Survey/:token" element={<LegacyInviteRedirect />} />
        <Route
          caseSensitive={false}
          path="/Completed/:token"
          element={<LegacyInviteRedirect />}
        />
        <Route path="/Survey-Pause" element={<SurveyPausePage />} />
        <Route path="/ops/admin-access" element={<LegacyRedirect to={ROUTES.adminAccess} />} />
        <Route path="/" element={<Navigate to={ROUTES.login} replace />} />
        <Route
          path={ROUTES.login}
          element={
            <PublicOnly>
              <LoginPage />
            </PublicOnly>
          }
        />
        <Route
          path={ROUTES.signup}
          element={
            <PublicOnly>
              <OnboardingPage />
            </PublicOnly>
          }
        />
        <Route path={ROUTES.join} element={<WorkspaceJoinPage />} />
        <Route
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route path={ROUTES.dashboard} element={<AdminDashboardPage />} />
          <Route path={ROUTES.nexusChat} element={<NexusChatPage />} />
          <Route path={ROUTES.portfolio} element={<PortfolioPage />} />
          <Route path={ROUTES.engagement} element={<EngagementPage />} />
          <Route path={ROUTES.studio} element={<StudioPage />} />
          <Route path={ROUTES.branding} element={<WorkspaceBrandingPage />} />
          <Route path={ROUTES.switchWorkspace} element={<SwitchWorkspacePage />} />
          <Route path={ROUTES.reportProfiles} element={<ReportProfilesPage />} />
          <Route path="/legacy/analytics" element={<AnalyticsPage />} />
          <Route path="/legacy/insights" element={<InsightsPage />} />
          <Route path="/legacy/invite" element={<EngagementWizard />} />
          <Route path="/legacy/invites" element={<InviteManagementPage />} />
          <Route path="/legacy/company-profile" element={<CompanyProfilePage />} />
          <Route path="/legacy/assessment-form" element={<AssessmentFormPage />} />
          <Route path="/legacy/report-profiles" element={<ReportProfilesPage />} />
          <Route
            path={ROUTES.adminRoles}
            element={
              <ProtectedRoute permissionAny={["admin:manage_roles", "admin:read"]}>
                <RoleManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.adminUsers}
            element={
              <ProtectedRoute permissionAny={["admin:manage_users", "admin:read"]}>
                <UserManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.adminEmailTemplates}
            element={
              <ProtectedRoute permission="templates:manage">
                <EmailTemplatesSchedulerPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.adminAccess}
            element={
              <ProtectedRoute permission="admin:manage_users">
                <AdminOpsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.surveyTemplates}
            element={
              <ProtectedRoute permission="assessment_form:manage">
                <SurveyTemplatesPage />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* Legacy /admin/* bookmarks */}
        <Route path="/admin" element={<LegacyRedirect to={ROUTES.dashboard} />} />
        <Route path="/admin/portfolio" element={<LegacyRedirect to={ROUTES.portfolio} />} />
        <Route path="/admin/engagement" element={<LegacyRedirect to={ROUTES.engagement} />} />
        <Route path="/admin/studio" element={<LegacyRedirect to={ROUTES.studio} />} />
        <Route path="/admin/report-profiles" element={<LegacyRedirect to={ROUTES.reportProfiles} />} />
        <Route path="/admin/analytics" element={<LegacyRedirect to={LENS_ROUTES.portfolioOperations} />} />
        <Route path="/admin/insights" element={<LegacyRedirect to={LENS_ROUTES.portfolioPerformance} />} />
        <Route path="/admin/invite" element={<LegacyRedirect to={ROUTES.engagement} />} />
        <Route path="/admin/invites" element={<LegacyRedirect to={LENS_ROUTES.engagementPipeline} />} />
        <Route path="/admin/company-profile" element={<LegacyRedirect to={LENS_ROUTES.studioCompanies} />} />
        <Route path="/admin/assessment-form" element={<LegacyRedirect to={LENS_ROUTES.studioSurvey} />} />
        <Route path="/admin/legacy/*" element={<LegacyAdminLegacyRedirect />} />

        <Route path="*" element={<p className="p-8 text-center">Page not found.</p>} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

function LegacyAdminLegacyRedirect() {
  const { pathname, search } = useLocation();
  const sub = pathname.replace(/^\/admin\/legacy\/?/, "") || "";
  const target = sub ? `/legacy/${sub}` : ROUTES.dashboard;
  return <Navigate to={`${target}${search}`} replace />;
}
